import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Panel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound } from "lucide-react";
import { buildInspectCommand, CommandBox } from "@/components/GatewayRelayPanel";

function PushCommand({ code }) {
  const { data } = useQuery({ queryKey: ["gateway-imports"], queryFn: async () => (await api.get("/gateway-transfer/imports")).data });
  const cmd = buildInspectCommand(data?.egress_harden_sha256 || "", "egress-harden.py", "egress_harden", `--push-bundle","${window.location.origin}","${code.code}`);
  return (
    <div className="space-y-1">
      <div className="text-slate-500">Run on the gateway (expires {code.expires_at.slice(11, 16)} UTC). Expect <span className="font-mono">PUSH … MATCH</span> and <span className="font-mono">RESULT exit=0</span>; this panel then shows Active.</div>
      {cmd && <CommandBox cmd={cmd} testId="gw-client-push-command" />}
    </div>
  );
}

async function stepUp(pw) {
  const { data } = await api.post("/auth/step-up/risk-control", /^\d{6}$/.test(pw) ? { totp_code: pw } : { password: pw });
  return data.step_up_token;
}

export function GatewayClientCertPanel() {
  const qc = useQueryClient();
  const [pw, setPw] = useState("");
  const [bundle, setBundle] = useState("");
  const done = (msg) => () => { toast.success(msg); setPw(""); setBundle(""); qc.invalidateQueries({ queryKey: ["gw-client"] }); };
  const fail = (e) => toast.error(formatApiError(e.response?.data?.detail));
  const csr = useMutation({ mutationFn: async () => (await api.post("/system/gateway-client/csr", { step_up_token: await stepUp(pw) })).data, onSuccess: done("Key generated inside Tradify; CSR ready"), onError: fail });
  const inst = useMutation({ mutationFn: async () => (await api.post("/system/gateway-client/certificate", { bundle, step_up_token: await stepUp(pw) })).data, onSuccess: done("Certificate installed"), onError: fail });
  const [code, setCode] = useState(null);
  const up = useMutation({ mutationFn: async () => (await api.post("/system/gateway-client/upload-code", { step_up_token: await stepUp(pw) })).data, onSuccess: (r) => { setCode(r); setPw(""); }, onError: fail });
  const s = useQuery({ queryKey: ["gw-client"], queryFn: async () => (await api.get("/system/gateway-client")).data, refetchInterval: (q) => (code && !q.state.data?.active && Date.parse(code.expires_at) > Date.now() ? 5000 : false) });
  const d = s.data;
  return (
    <Panel title="Gateway client certificate (key never leaves Tradify)" testId="gw-client-panel" actions={<KeyRound className="w-4 h-4 text-slate-500" />}>
      <div className="p-4 space-y-3 text-xs">
        <div data-testid="gw-client-status" className="text-slate-300">
          {d?.active ? `Active · sha256 ${d.fingerprint_sha256.slice(0, 16)}… · expires ${d.not_after?.slice(0, 10)}` : "No certificate installed"}
          {d?.pending_csr ? " · CSR pending" : ""}
        </div>
        <Input data-testid="gw-client-reverify" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password / code" className="h-8 max-w-xs bg-[var(--bg-canvas)] border-[var(--border-subtle)]" />
        <Button data-testid="gw-client-csr-btn" size="sm" variant="outline" disabled={!pw || csr.isPending} onClick={() => csr.mutate()}>1 · Generate key + CSR</Button>
        {d?.csr_pem && (
          <div className="space-y-1">
            <div className="text-slate-500">Public CSR — copy to the gateway file /root/tradify-app.csr (safe to share; contains no key):</div>
            <textarea data-testid="gw-client-csr" readOnly value={d.csr_pem} className="w-full h-28 font-mono text-[10px] bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-slate-300" />
            <Button data-testid="gw-client-csr-copy" size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(d.csr_pem); toast.success("CSR copied"); }}>Copy CSR</Button>
          </div>)}
        {d?.pending_csr && (
          <div className="space-y-1" data-testid="gw-client-push">
            <div className="text-slate-400">Recommended: let the gateway send the signed PUBLIC bundle itself (single-use code, 15 min; fingerprint verified on both ends; a failed push uses up the code — just create a new one).</div>
            <Button data-testid="gw-client-upload-code-btn" size="sm" variant="outline" disabled={!pw || up.isPending} onClick={() => up.mutate()}>Create push command</Button>
            {code && <PushCommand code={code} />}
          </div>)}
        {d?.pending_csr && (
          <div className="space-y-1">
            <div className="text-slate-500">Or paste PUBLIC_CERT_BUNDLE_B64 manually from <span className="font-mono">egress_harden.py --sign-csr</span>:</div>
            <textarea data-testid="gw-client-bundle" value={bundle} onChange={(e) => setBundle(e.target.value)} className="w-full h-20 font-mono text-[10px] bg-[var(--bg-canvas)] border border-[var(--border-subtle)] rounded p-2 text-slate-300" />
            <Button data-testid="gw-client-install-btn" size="sm" disabled={!pw || !bundle || inst.isPending} onClick={() => inst.mutate()}>2 · Install certificate</Button>
          </div>)}
      </div>
    </Panel>
  );
}
